import React, { useContext, useEffect, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { ControlRoomContext } from './ControlPage'

const priorityStyle = {
  Critical: { bg: '#FCE7F3', color: '#85276A' },
  High: { bg: '#FEE2E2', color: '#E32525' },
  Moderate: { bg: '#FFEDD5', color: '#F28B1F' },
  Low: { bg: '#DCFCE7', color: '#00964C' },
}

const AllIncidents = ({ open, onClose, controlRoomID, controlRoomRef }) => {
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [incidents, setIncidents] = useState([])
  const [incidentSearch, setIncidentSearch] = useState('')

  const { setSelectedIncident } = useContext(ControlRoomContext)

  const filteredIncidents = incidents.filter((incident) => {
    const search = incidentSearch.trim()
    if (!search) return true

    const incidentNumber = String(incident.incidentNumber ?? '')
    const displayNumber = incidentNumber.slice(-4)

    return (
      incidentNumber.includes(search) ||
      displayNumber.includes(search.padStart(4, '0')) ||
      displayNumber.includes(search)
    )
  })

  useEffect(() => {
    if (!open) return

    setLoading(true)
    setFetchError(false)

    API.get(`/commandcontrol/allincidents/${controlRoomID}`)
      .then((res) => {
        if (res.status === 200) {
          setIncidents(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [open, controlRoomID])

  return (
    <BigModal
      open={open}
      onClose={onClose}
      style={{ width: { xs: '94vw', md: '80%' }, maxWidth: { xs: '94vw', md: '90vw' } }}
    >
      <Box sx={{ width: '100%', minWidth: { md: 760 } }}>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 3,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #E32525 155%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 30 }} />
            </Box>

            <Stack>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Incident Register
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                All Incidents
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                View all incidents recorded against this control room.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Search incident number"
            value={incidentSearch}
            onChange={(event) => setIncidentSearch(event.target.value)}
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'white',
              },
            }}
          />

          {loading && (
            <LinearProgress
              sx={{
                borderRadius: 999,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#239AA4',
                },
              }}
            />
          )}

          {fetchError && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              Unable to load incidents. Please try again later.
            </Alert>
          )}

          <CustomDataGrid
            rows={filteredIncidents}
            loading={loading}
            columns={[
              {
                field: 'incidentNumber',
                headerName: 'ID',
                minWidth: 150,
                valueGetter: (value, row) => String(value).slice(-4),
              },
              {
                field: 'description',
                headerName: 'Summary',
                flex: 1,
                minWidth: 220,
              },
              {
                field: 'presentingComplaint',
                headerName: 'Presenting Complaint',
                minWidth: 190,
              },
              {
                field: 'incidentType',
                headerName: 'Type',
                minWidth: 140,
              },
              {
                field: 'priority',
                headerName: 'Priority',
                minWidth: 140,
                renderCell: ({ value }) => {
                  const style = priorityStyle[value] || priorityStyle.Low

                  return (
                    <Chip
                      label={value || 'Unknown'}
                      size="small"
                      sx={{
                        borderRadius: 999,
                        backgroundColor: style.bg,
                        color: style.color,
                        fontWeight: 900,
                      }}
                    />
                  )
                },
              },
              {
                field: 'incidentStatus',
                headerName: 'Status',
                valueGetter: (value, row) => {
                  return row.incidentStatus
                },
                minWidth: 140,
                renderCell: ({ row }) => {
                  const value = row?.status
                  return (
                    <Chip
                      label={value || 'Unknown'}
                      size="small"
                      sx={{
                        borderRadius: 999,
                        backgroundColor:
                          value === 'New'
                            ? '#FEE2E2'
                            : value === 'Active'
                              ? '#FFEDD5'
                              : '#E2E8F0',
                        color:
                          value === 'New'
                            ? '#991B1B'
                            : value === 'Active'
                              ? '#9A3412'
                              : '#334155',
                        fontWeight: 800,
                      }}
                    />
                  )
                },
              },
            ]}
            getRowId={(row) => row?.id}
            slots={{
              toolbar: () => null,
              footer: () => null,
              noRowsOverlay: () => (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  No incidents have been recorded yet.
                </Alert>
              ),
            }}
            disableRowSelectionOnClick
            onRowClick={({ row }) => {
              setSelectedIncident(row)
              onClose()
            }}
          />
        </Stack>
      </Box>
    </BigModal>
  )
}

export default AllIncidents
